import { lazy, Suspense, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Heart, User, Image as ImageIcon, Sparkles } from "lucide-react";
import type { Shelter } from "@/types/shelter";

const ShareNeedsModal = lazy(() =>
  import("@/components/ShareNeedsModal").then((m) => ({ default: m.ShareNeedsModal }))
);
const ShareProfileModal = lazy(() =>
  import("@/components/ShareProfileModal").then((m) => ({ default: m.ShareProfileModal }))
);

const Graphics = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: owned } = useOwnedShelters(user?.id);
  const [shareType, setShareType] = useState<"needs" | "profile" | null>(null);

  const ownedMatch = owned?.find((s) => s.slug === slug);

  useEffect(() => {
    if (owned && !ownedMatch && slug) {
      navigate("/panel", { replace: true });
    }
  }, [owned, ownedMatch, slug, navigate]);

  // Pełne dane schroniska (lat/lng/postal_code itd.) wymagane przez modale grafik.
  const { data: shelter, isLoading } = useQuery({
    queryKey: ["panel-graphics-shelter", ownedMatch?.id],
    enabled: !!ownedMatch?.id,
    queryFn: async (): Promise<Shelter | null> => {
      const { data, error } = await supabase
        .from("shelters")
        .select("*")
        .eq("id", ownedMatch!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as Shelter | null) ?? null;
    },
  });

  if (!ownedMatch || isLoading || !shelter) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasNeeds = !!(shelter.current_needs && shelter.current_needs.trim().length > 0);

  return (
    <>
      <Helmet>
        <title>Grafiki - {shelter.name} | Panel</title>
      </Helmet>
      <div className="max-w-3xl mx-auto space-y-2.5 sm:space-y-5">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight">
            <span className="lg:hidden">Grafiki</span>
            <span className="hidden lg:inline">Grafiki - {shelter.name}</span>
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Pobierz gotową grafikę i udostępnij ją w mediach społecznościowych.
          </p>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 p-3">
          <Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0" />
          <p className="text-sm text-muted-foreground">
            Każda grafika jest generowana automatycznie z aktualnych danych Twojego profilu.
            Możesz wybrać format Post lub Story i pobrać plik PNG.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Card className="group transition-shadow hover:shadow-md">
            <CardContent className="p-3 sm:p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Heart className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-semibold leading-tight">Aktualne potrzeby</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Lista rzeczy, których schronisko potrzebuje
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShareType("needs")}
                disabled={!hasNeeds}
                className="w-full"
                size="sm"
              >
                <ImageIcon className="w-4 h-4 mr-1.5" />
                Wybierz i pobierz
              </Button>
              {!hasNeeds && (
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Uzupełnij sekcję „Aktualne potrzeby", aby wygenerować grafikę.{" "}
                  <Link to={`/panel/potrzeby/${shelter.slug}`} className="text-primary hover:underline">
                    Edytuj potrzeby →
                  </Link>
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="group transition-shadow hover:shadow-md">
            <CardContent className="p-3 sm:p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-semibold leading-tight">Profil schroniska</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Informacje kontaktowe, wolontariat, opis
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShareType("profile")}
                className="w-full"
                size="sm"
              >
                <ImageIcon className="w-4 h-4 mr-1.5" />
                Wybierz i pobierz
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {shareType === "needs" && (
        <Suspense fallback={null}>
          <ShareNeedsModal
            shelter={shelter}
            controlledOpen={true}
            onControlledOpenChange={(open) => !open && setShareType(null)}
            skipSelection
          />
        </Suspense>
      )}

      {shareType === "profile" && (
        <Suspense fallback={null}>
          <ShareProfileModal
            shelter={shelter}
            isOpen={true}
            onOpenChange={(open) => !open && setShareType(null)}
          />
        </Suspense>
      )}
    </>
  );
};

export default Graphics;
